/**
 * The seam, mounted at the ROOT of the console rather than around one subtree.
 *
 * WHY THIS EXISTS AT ALL. `ClientApiBoundary` mounted `FleetApiProvider` around
 * `src/screens/client/**` only, and said so in its own docstring: "it is per-subtree rather
 * than per-app, so a screen outside `src/screens/client/**` still has no API ... that is not a
 * bug this file should hide; it is unfinished bootstrap". This is the bootstrap being finished.
 * Two things were waiting on it and neither could be built without it:
 *
 *  - The fleet, deployment and library screens cannot migrate off the mock while the only
 *    provider in the tree sits below them.
 *  - The CHROME cannot read anything. A notification bell that reports real fleet attention
 *    items lives in `TopBar`, above every route, so it is above the client boundary too.
 *
 * `useFleetApi()` resolves to the NEAREST provider, so mounting this above the existing one
 * makes that one redundant WITHOUT changing it. Nothing in `src/screens/client/**` has to move,
 * which matters because that is another lane's directory.
 *
 * WHY `getToken` IS A PROP AND NOT AN IMPORT, and it is not a style preference. `resolveToken`
 * lives in `src/auth/`, whose module graph reaches `@azure/msal-browser` through a static
 * import. `App.tsx` keeps that out of the entry chunk on purpose -- measured at the time as
 * 864 kB versus 1.1 MB, with `PublicClientApplication` in the demo path's entry in plain text.
 * If this module imported the token source directly, every console that renders the mock would
 * pay for MSAL again and that comment in `App.tsx` would quietly stop being true. So the
 * mounting site supplies it: the authenticated tree passes `resolveToken`, the demo tree passes
 * nothing and gets the mock, and neither knows about the other.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { EmptyState } from '../components/ui/EmptyState'
import { configuredBaseUrl } from './create-fleet-api.ts'
import { sessionApi } from './session-api.ts'
import type { TokenSource } from './http-fleet-api.ts'
import type { FleetApi } from './fleet-api.ts'
import { FleetApiContext } from './context.ts'

export interface AppApiBoundaryProps {
  children: ReactNode
  /**
   * How the transport asks for a bearer. Absent on the demo path, where `createFleetApi`
   * returns the mock and never calls it. Absent is NOT a development bypass: with a base URL
   * configured and no token source, the HTTP client refuses its own first call with a
   * re-authentication signal rather than rendering an empty console (wf-12 U-12).
   */
  getToken?: TokenSource
  /** Called on a 401, so the sign-in surface can react once rather than per request. */
  onUnauthorized?: () => void
}

export function AppApiBoundary({ children, getToken, onUnauthorized }: AppApiBoundaryProps) {
  const [api, setApi] = useState<FleetApi | null>(null)
  const [failed, setFailed] = useState<Error | null>(null)

  useEffect(() => {
    let live = true
    sessionApi(getToken, onUnauthorized).then(
      (resolved) => live && setApi(resolved),
      (error: unknown) => live && setFailed(error instanceof Error ? error : new Error(String(error))),
    )
    return () => {
      live = false
    }
    // Deliberately once: the implementation is chosen by the environment, not by a prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (failed) {
    return (
      <EmptyState
        icon="triangle-alert"
        eyebrow="NO API"
        title={
          `The console could not build a client for ${configuredBaseUrl() ?? 'the mock fleet'}: ` +
          `${failed.message}. Nothing below this point can be trusted, so nothing is rendered.`
        }
      />
    )
  }

  /*
   * THE CHROME RENDERS IMMEDIATELY. This boundary does NOT gate its children on the API
   * resolving, and that is a deliberate difference from `ClientApiBoundary`, which does.
   *
   * The difference is what sits underneath each one. `ClientApiBoundary` wraps screens, and a
   * screen with no API must not render, because it would render as though it had asked and got
   * nothing. This one wraps the whole console including the rail, the breadcrumb and the search
   * box -- none of which have anything to do with the fleet, and all of which are correct
   * before any API exists.
   *
   * Blocking here was measurably wrong rather than merely slower: it put an EmptyState where
   * the entire console should be until a dynamically imported chunk arrived, so the demo path
   * had no navigation at all on first paint. `console.spec.ts`'s "every rail item navigates to
   * a screen rather than a dead end" caught it by finding zero rail links, which is exactly
   * what a person would have seen.
   *
   * `FleetApiContext` is already typed `FleetApi | null`, so a null value is the honest
   * "not resolved yet" and needs no new shape. `useOptionalFleetApi()` returns it and the
   * panels say so in words; `useFleetApi()` still throws, which is why a SCREEN that needs a
   * guaranteed API keeps its own blocking boundary rather than relying on this one.
   */
  return <FleetApiContext.Provider value={api}>{children}</FleetApiContext.Provider>
}
